// Environment Detection Utility
// Matches backend environment detection logic for consistent behavior

export interface EnvironmentConfig {
    environment: string;
    audience: string;
    domain: string;
    clientIdSuffix: string;
}

/**
 * Detect current environment based on URL and deployment context
 * Matches the backend getCurrentEnvironment() logic
 */
export function getCurrentEnvironment(): string {
    // Check if we're in a Vercel deployment
    if (import.meta.env.VITE_VERCEL_PROJECT_NAME) {
        return import.meta.env.VITE_VERCEL_PROJECT_NAME;
    }

    // Check current URL for environment detection
    const currentUrl = window.location.origin;

    if (currentUrl.includes('ops.memer.world')) {
        return 'memer-operations-prod';
    } else if (currentUrl.includes('dev.memer.world')) {
        return 'memer-operations-dev';
    } else if (currentUrl.includes('localhost')) {
        return 'local-development';
    }

    // Default fallback
    return 'memer-operations-dev';
}

/**
 * Trim whitespace and newlines from environment variables
 * Fixes issues with GPG-loaded variables that may contain trailing whitespace
 * CRITICAL FIX: Strip shell script artifacts like '-n ' or '-e ' prefix
 */
function trimEnvVar(value: string | undefined): string | undefined {
    return value ? value.trim().replace(/^-n\s+/, '').replace(/^-e\s+/, '') : value;
}

/**
 * Get Auth0 configuration for current environment
 */
export function getAuth0Config(): EnvironmentConfig {
    const environment = getCurrentEnvironment();

    const configs: Record<string, EnvironmentConfig> = {
        'memer-operations-dev': {
            environment: 'dev',
            audience: trimEnvVar(import.meta.env.VITE_AUTH0_AUDIENCE_DEV) || 'https://dev.memer.world/api',
            domain: trimEnvVar(import.meta.env.VITE_AUTH0_DOMAIN_DEV) || 'dev-l00eyle3qdyqapdy.us.auth0.com',
            clientIdSuffix: 'DEV'
        },
        'local-development': {
            environment: 'local',
            audience: trimEnvVar(import.meta.env.VITE_AUTH0_AUDIENCE_LOCAL) || 'https://dev.memer.world/api',
            domain: trimEnvVar(import.meta.env.VITE_AUTH0_DOMAIN_LOCAL) || 'dev-l00eyle3qdyqapdy.us.auth0.com',
            clientIdSuffix: 'LOCAL'
        },
        'memer-operations-prod': {
            environment: 'prod',
            audience: trimEnvVar(import.meta.env.VITE_AUTH0_AUDIENCE_PROD) || 'https://ops.memer.world/api',
            domain: trimEnvVar(import.meta.env.VITE_AUTH0_DOMAIN_PROD) || 'dev-l00eyle3qdyqapdy.us.auth0.com',
            clientIdSuffix: 'PROD'
        }
    };

    const config = configs[environment];

    if (!config) {
        console.warn(`⚠️ Unknown environment: ${environment}, falling back to dev`);
        return configs['memer-operations-dev'];
    }

    console.log(`🌍 Environment detected: ${environment}`, {
        audience: config.audience,
        domain: config.domain
    });

    return config;
}

/**
 * Get the appropriate Auth0 audience for the current environment
 */
export function getAuth0Audience(): string {
    return getAuth0Config().audience;
}

/**
 * Debug function to log current environment info
 */
export function logEnvironmentInfo(): void {
    const config = getAuth0Config();
    console.log('🔍 Environment Detection Debug:', {
        currentUrl: window.location.origin,
        detectedEnvironment: getCurrentEnvironment(),
        config: config,
        envVars: {
            VITE_VERCEL_PROJECT_NAME: import.meta.env.VITE_VERCEL_PROJECT_NAME,
            NODE_ENV: import.meta.env.NODE_ENV,
            MODE: import.meta.env.MODE
        }
    });
}
